/**
 * AccountsList — main `/accounts` page.
 *
 * Shared by the client portal (single tenant) and the internal portal
 * (cross-tenant). Provides Table (default) + Kanban view, a config-driven
 * status board, search/phase/state/sort filters (plus an internal-only client
 * picker), cursor pagination, CSV export, and a "+ New Account" button.
 */

import React from 'react';
import {useLocation, useNavigate, useSearchParams} from 'react-router-dom';
import {Plus, Briefcase, LayoutDashboardIcon, Download, GitBranch, LayoutList} from 'lucide-react';
import {Button} from '@common-web/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@common-web/components/ui/select';
import {PageHeader} from '@common-web/components/page-header';
import {trpc} from '@common-web/lib/trpc';
import {useIsInternal} from '@common-web/providers/portalProvider';
import {useAuthStore} from '@common-web/stores/authStore';
import {CODE_PERMISSIONS} from '@common/permissions';
import {useHasPermission, useClientActionAllowed} from '@common-web/hooks/use-permissions';
import {formatCurrency} from '@common/lib/utils';
import {useAccountsViewConfig} from '@common-web/hooks/useAccountsViewConfig';
import {
  ACCOUNTS_SUB_STAGES,
  findSubStageByKey,
  isForwardSubStageMove,
  resolveAccountPosition,
  visibleStages,
  type AccountsSubStageKey,
} from '@common/accountsView';
import {cbsStatusTone} from '@common-web/components/account/cbsAccountsPresentation';
import {createCsv} from '@common-web/lib/createCsv';
import {downloadCsvFile, makeCsvFilename} from '@common-web/lib/downloadCsv';
import {toast} from '@common-web/hooks/use-toast';
import type {AccountsSubStage, LifecyclePhase} from '@api/generated/client';
import {AccountKanban} from './components/AccountKanban';
import {AccountTable} from './components/AccountTable';
import {
  AccountFilterBar,
  DEFAULT_ACCOUNT_FILTERS,
  type AccountFilters,
} from './components/AccountFilterBar';
import {AccountsStatusBoard} from './components/AccountsStatusBoard';
import {NewAccountModal} from './components/NewAccountModal';
import {
  SkipConfirmModal,
  type BoardMoveDetails,
  type PendingBoardMove,
} from './components/SkipConfirmModal';
import {BrokerScopeControls, type BrokerScope} from '../broker/BrokerScopeControls';
import {
  ARCHIVE_REASON_LABEL,
  PHASE_ORDER,
  relativeTime,
} from '@common-web/components/account/phaseLabels';
import {READY_FOR_CBS_STATUSES} from '@common-web/components/rfc/RfcSubmissionsList';
import {useDebounce} from '@common-web/hooks/use-debounce';
import type {AccountCardData} from './components/AccountCard';

interface AccountsListProps {
  routePrefix?: string;
}

const PAGE_SIZE = 100;

// Internal page-size options mirror the cbs-portal demo's pagination control.
const INTERNAL_PAGE_SIZES = [25, 50, 100] as const;

export function AccountsList({routePrefix = '/accounts'}: AccountsListProps): React.ReactElement {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const utils = trpc.useUtils();
  const isInternal = useIsInternal();
  const hasInternalProfile = useAuthStore((s) => !!s.user?.cbsInternalProfile);
  const hasCrossTenantAccess = useHasPermission(CODE_PERMISSIONS.tenant.accessAll.key);
  const isInternalAdmin = hasInternalProfile && hasCrossTenantAccess;
  const isBrokerUser = useAuthStore((s) => s.isBrokerUser());
  const canCreateAccount = useClientActionAllowed(CODE_PERMISSIONS.accounts.create.key);
  const canExportAccounts = useClientActionAllowed(CODE_PERMISSIONS.accounts.export.key);
  const config = useAccountsViewConfig();

  const [view, setView] = React.useState<'kanban' | 'table'>('table');
  // Internal portal exposes the demo's 25/50/100 rows-per-page control;
  // client portals keep the fixed page size.
  const [pageSize, setPageSize] = React.useState<number>(isInternal ? 25 : PAGE_SIZE);
  // Deep links (e.g. from "My Team": /accounts?broker=<id>&active=true) seed the
  // initial filter state so the list lands pre-filtered on a downline broker.
  const [filters, setFilters] = React.useState<AccountFilters>(() => {
    const brokerParam = searchParams.get('broker');
    const phaseParam = searchParams.get('phase');
    const activeParam = searchParams.get('active') === 'true';
    // `phase` deep-links to any lifecycle phase (e.g. My Team's Onboarding /
    // Active Groups columns); `active=true` is the legacy shortcut for the
    // ACTIVE phase.
    const seededPhase: LifecyclePhase[] = PHASE_ORDER.includes(phaseParam as LifecyclePhase)
      ? [phaseParam as LifecyclePhase]
      : activeParam
        ? ['ACTIVE']
        : [];
    // `subStage` deep-links to specific sub-stages (e.g. My Team's "Prospects"
    // column, which targets the Prospect stage while excluding the separate SWOT
    // Call stage). Comma-separated, validated against the canonical set.
    const subStageParam = searchParams.get('subStage');
    const seededSubStages: AccountsSubStage[] = subStageParam
      ? (subStageParam
          .split(',')
          .filter((s) =>
            (ACCOUNTS_SUB_STAGES as readonly string[]).includes(s),
          ) as AccountsSubStage[])
      : [];
    if (!brokerParam && seededPhase.length === 0 && seededSubStages.length === 0) {
      return DEFAULT_ACCOUNT_FILTERS;
    }
    return {
      ...DEFAULT_ACCOUNT_FILTERS,
      assignedBrokerId: brokerParam || null,
      phase: seededPhase,
      subStages: seededSubStages,
    };
  });
  // Broker scope (own vs downline). Honored server-side only for broker callers;
  // a deep-linked `?broker=` lands the upline directly in team scope.
  const [scope, setScope] = React.useState<BrokerScope>(
    searchParams.get('broker') ? 'team' : 'mine',
  );
  const [statusKeys, setStatusKeys] = React.useState<string[]>([]);

  // Debounce the free-text inputs so each keystroke doesn't refetch.
  const debouncedSearch = useDebounce(filters.search, 300);
  const debouncedFein = useDebounce(filters.fein, 300);
  const debouncedGroupName = useDebounce(filters.groupName, 300);
  const debouncedGroupId = useDebounce(filters.groupId, 300);
  const debouncedAgentSearch = useDebounce(filters.agentSearch, 300);
  const [pendingMove, setPendingMove] = React.useState<PendingBoardMove | null>(null);

  const isNewRoute = location.pathname.endsWith('/new');

  // Internal portal spans every tenant; load the client list for the picker.
  const clientsQuery = trpc.internal.client.list.useQuery(undefined, {enabled: isInternal});
  const clientOptions = React.useMemo(
    () => (clientsQuery.data ?? []).map((c) => ({id: c.id, name: c.name})),
    [clientsQuery.data],
  );

  const sharedFilterInput = {
    search: debouncedSearch || undefined,
    fein: debouncedFein || undefined,
    phase: filters.phase.length > 0 ? filters.phase : undefined,
    subStage: filters.subStages.length > 0 ? filters.subStages : undefined,
    // Off-pipeline statuses (On Hold / Lost) selected in the Status filter.
    archivedStatuses: filters.archivedStatuses.length > 0 ? filters.archivedStatuses : undefined,
    assignedBrokerId: filters.assignedBrokerId ? [filters.assignedBrokerId] : undefined,
    // Scope only applies to broker callers; the API ignores it for admins.
    scope: isBrokerUser ? scope : undefined,
    state: filters.state || undefined,
    // Internal advanced search (cbs-portal parity); always empty on
    // client portals since the fields don't render there.
    clientIds: filters.clientIds.length > 0 ? filters.clientIds : undefined,
    agentSearch: debouncedAgentSearch.trim() || undefined,
    tags: filters.tags.length > 0 ? filters.tags : undefined,
    groupName: debouncedGroupName.trim() || undefined,
    groupId: debouncedGroupId.trim() || undefined,
    estimatedStartFrom: filters.estimatedStartFrom || undefined,
    estimatedStartTo: filters.estimatedStartTo || undefined,
    // Analyzer vs Monthly group designation; null = no filter.
    groupProcessType: filters.groupProcessType ?? undefined,
  };

  const list = trpc.account.list.useInfiniteQuery(
    {
      ...sharedFilterInput,
      viewSubStageKey: statusKeys.length > 0 ? statusKeys : undefined,
      sortBy: filters.sortBy,
      take: pageSize,
    },
    {getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined},
  );

  const counts = trpc.account.stageCounts.useQuery(sharedFilterInput);

  // Same query the New Group wizard and CBS Queue page use — RFC submissions
  // awaiting group creation, not RFC Received accounts (those are already built).
  const queueQuery = trpc.clients.rfc.listForAdmin.useQuery(
    {
      page: 1,
      limit: 1,
      sortKey: 'createdAt',
      sortDirection: 'asc',
      statuses: [...READY_FOR_CBS_STATUSES],
    },
    {enabled: isInternal, refetchOnWindowFocus: true},
  );
  const queueCount = queueQuery.data?.pagination.total ?? 0;

  const items: AccountCardData[] = React.useMemo(
    () => (list.data?.pages.flatMap((p) => p.items) ?? []) as AccountCardData[],
    [list.data],
  );

  // Internal filter card shows a "{shown} of {total}" result count; the total
  // is every account matching the filter bar (stage counts ignore the
  // status-board `viewSubStageKey` narrowing, matching cbs-portal semantics).
  // Mirrors the list's status branching: selected On Hold / Lost statuses add
  // their counts; selecting only those statuses replaces the pipeline total.
  const totalCount = React.useMemo(() => {
    const pipelineTotal = Object.values(counts.data?.stageCounts ?? {}).reduce((a, b) => a + b, 0);
    if (filters.archivedStatuses.length === 0) return pipelineTotal;
    const archivedTotal = filters.archivedStatuses.reduce(
      (sum, status) => sum + (counts.data?.archivedStatusCounts?.[status] ?? 0),
      0,
    );
    const pipelineNarrowed = filters.subStages.length > 0 || filters.phase.length > 0;
    return pipelineNarrowed ? pipelineTotal + archivedTotal : archivedTotal;
  }, [counts.data, filters.archivedStatuses, filters.subStages, filters.phase]);

  const transitionPhase = trpc.account.transitionPhase.useMutation({
    onSuccess: (_data, variables) => {
      utils.account.list.invalidate();
      utils.account.stageCounts.invalidate();
      utils.account.getById.invalidate({accountsId: variables.accountsId});
      setPendingMove(null);
    },
    onError: (err) => {
      toast({title: 'Could not move account', description: err.message, variant: 'destructive'});
      setPendingMove(null);
    },
  });

  const setViewSubStage = trpc.account.setViewSubStage.useMutation({
    onSuccess: (_data, variables) => {
      utils.account.list.invalidate();
      utils.account.stageCounts.invalidate();
      utils.account.getById.invalidate({accountsId: variables.accountsId});
      setPendingMove(null);
    },
    onError: (err) => {
      toast({title: 'Could not move account', description: err.message, variant: 'destructive'});
      setPendingMove(null);
    },
  });

  // Status-board drags between configured statuses. A forward drag may pass
  // over stages, which must each be explicitly resolved (date or
  // skip-with-reason) — the confirm modal collects them; a backward/same-stage
  // reposition needs nothing extra. The board doesn't know the account's
  // canonical position authoritatively, so forwardness is checked against the
  // in-memory card; the modal (and the server) re-derive it from the account.
  const handleAttemptSubStageMove = (accountsId: string, viewSubStageKey: string): void => {
    const card = items.find((a) => a.id === accountsId);
    const targetSys = findSubStageByKey(config, viewSubStageKey)?.subStage.systemSubStage;
    const forward =
      !!card &&
      !!targetSys &&
      isForwardSubStageMove(card.subStage as AccountsSubStageKey, targetSys);
    if (forward) {
      setPendingMove({kind: 'viewSubStage', accountsId, viewSubStageKey});
    } else {
      setViewSubStage.mutate({accountsId, viewSubStageKey});
    }
  };

  const isAdjacent = (from: LifecyclePhase, to: LifecyclePhase): boolean => {
    const i = PHASE_ORDER.indexOf(from);
    const j = PHASE_ORDER.indexOf(to);
    return Math.abs(i - j) === 1;
  };

  const canMoveStage = useClientActionAllowed(
    CODE_PERMISSIONS.accounts.movePipelineStageManual.key,
  );

  const handleAttemptMove = (
    accountsId: string,
    fromPhase: LifecyclePhase,
    toPhase: LifecyclePhase,
  ): void => {
    if (fromPhase === toPhase) return;
    if (!canMoveStage) {
      toast({
        title: 'Permission required',
        description: 'You do not have permission to move accounts between stages',
        variant: 'destructive',
      });
      return;
    }
    // Every forward drag confirms through the modal — it collects the move's
    // effective date plus a resolution for each passed-over sub-stage (the
    // server rejects the jump without them). Backward drags only confirm when
    // they skip phases.
    const forward = PHASE_ORDER.indexOf(toPhase) > PHASE_ORDER.indexOf(fromPhase);
    if (!forward && isAdjacent(fromPhase, toPhase)) {
      transitionPhase.mutate({accountsId, targetPhase: toPhase});
    } else {
      setPendingMove({kind: 'phase', accountsId, fromPhase, toPhase});
    }
  };

  const handleConfirmMove = (details: BoardMoveDetails): void => {
    if (!pendingMove) return;
    const resolutionInputs = {
      stageResolutions: details.stageResolutions.length > 0 ? details.stageResolutions : undefined,
      effectiveDate: details.effectiveDate ?? undefined,
    };
    if (pendingMove.kind === 'phase') {
      transitionPhase.mutate({
        accountsId: pendingMove.accountsId,
        targetPhase: pendingMove.toPhase,
        ...resolutionInputs,
      });
    } else {
      setViewSubStage.mutate({
        accountsId: pendingMove.accountsId,
        viewSubStageKey: pendingMove.viewSubStageKey,
        ...resolutionInputs,
      });
    }
  };

  const toggleStatusKey = (key: string): void => {
    setStatusKeys((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  };

  // cbs-portal's "Filtered by status" chips: label + tone for each
  // status-board sub-stage currently narrowing the list, surfaced near the
  // table so the filter stays visible after scrolling past the board.
  const statusChips = React.useMemo(() => {
    if (!isInternal || statusKeys.length === 0) return [];
    const selected = new Set(statusKeys);
    return visibleStages(config).flatMap((stage) =>
      stage.subStages
        .filter((sub) => selected.has(sub.key))
        .map((sub) => ({
          key: sub.key,
          label: sub.label,
          tone: cbsStatusTone(sub.systemSubStage, stage.systemPhase),
        })),
    );
  }, [isInternal, statusKeys, config]);

  const [exporting, setExporting] = React.useState(false);

  const handleExport = async (): Promise<void> => {
    setExporting(true);
    try {
      // Fetch every page matching the current filters — the on-screen list is
      // paginated, but an export must cover the full result set.
      const rows: AccountCardData[] = [];
      let cursor: string | null | undefined;
      do {
        const page = await utils.account.list.fetch({
          ...sharedFilterInput,
          viewSubStageKey: statusKeys.length > 0 ? statusKeys : undefined,
          sortBy: filters.sortBy,
          take: PAGE_SIZE,
          cursor,
        });
        rows.push(...(page.items as AccountCardData[]));
        cursor = page.nextCursor;
      } while (cursor);

      if (rows.length === 0) {
        toast({title: 'Nothing to export', description: 'No accounts match the current filters.'});
        return;
      }
      const records = rows.map((a) => {
        const position = resolveAccountPosition(config, a);
        const members = a.group?.estimatedMemberCount ?? a.estimatedFtEmployees;
        return {
          ...(isInternal ? {Client: a.client?.name ?? ''} : {}),
          Company: a.companyName,
          Phase: position?.stage.label ?? a.lifecyclePhase,
          'Sub-stage': position?.subStage.label ?? a.subStage,
          'Group #': a.group?.vbaGroupId ?? '',
          Members: members !== null ? String(members) : '',
          State: a.state ?? '',
          'Assigned Broker': a.assignedBroker?.name ?? '',
          'Deal Value': formatCurrency(a.dealValue, {fallback: '', maximumFractionDigits: 0}),
          'Next Action': a.nextActionText ?? '',
          'Last Activity': relativeTime(a.lastActivityAt),
          // On Hold / Lost rows carry their off-pipeline status when those
          // statuses are part of the current filter.
          ...(filters.archivedStatuses.length > 0
            ? {
                Status:
                  a.isArchived && a.archiveReason ? ARCHIVE_REASON_LABEL[a.archiveReason] : '',
              }
            : {}),
        };
      });
      const csv = createCsv(records, Object.keys(records[0]));
      downloadCsvFile(csv, makeCsvFilename('accounts'));
    } catch (err) {
      toast({
        title: 'Export failed',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className='flex flex-col h-full'>
      <PageHeader
        title='Accounts'
        description={
          isInternal
            ? 'All accounts across every onboarding stage and the active book'
            : 'Track every company in your book — from first conversation to active client.'
        }
        icon={
          isInternal ? (
            <Briefcase className='h-6 w-6' />
          ) : (
            <LayoutDashboardIcon className='h-6 w-6' />
          )
        }
      >
        {isInternal ? (
          /* cbs-portal hero: segmented List | Pipeline control with an
             orange-soft active fill (CBSGroupList's view toggle). */
          <div className='flex overflow-hidden rounded-lg border'>
            <button
              type='button'
              onClick={() => setView('table')}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-semibold transition-colors ${
                view === 'table'
                  ? 'bg-primary/10 text-primary'
                  : 'bg-card text-muted-foreground hover:bg-muted'
              }`}
            >
              <LayoutList size={14} /> List
            </button>
            <button
              type='button'
              onClick={() => setView('kanban')}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-semibold transition-colors ${
                view === 'kanban'
                  ? 'bg-primary/10 text-primary'
                  : 'bg-card text-muted-foreground hover:bg-muted'
              }`}
            >
              <GitBranch size={14} /> Pipeline
            </button>
          </div>
        ) : (
          <div className='flex items-center rounded-md border'>
            <Button
              variant={view === 'kanban' ? 'default' : 'ghost'}
              size='sm'
              className='rounded-r-none'
              onClick={() => setView('kanban')}
            >
              Kanban
            </Button>
            <Button
              variant={view === 'table' ? 'default' : 'ghost'}
              size='sm'
              className='rounded-l-none'
              onClick={() => setView('table')}
            >
              Table
            </Button>
          </div>
        )}
        {canExportAccounts && (
          <Button variant='outline' size='sm' onClick={handleExport} disabled={exporting}>
            <Download className='w-4 h-4 mr-1' />
            {exporting ? 'Exporting…' : 'Export'}
          </Button>
        )}
        {canCreateAccount && (
          <div className='relative'>
            <Button onClick={() => navigate(`${routePrefix}/new`)}>
              <Plus className='w-4 h-4 mr-1' />
              {isInternal ? 'Add Group' : 'New Account'}
            </Button>
            {isInternal && queueCount > 0 ? (
              <span
                className='absolute -top-2 -right-2 flex h-[19px] min-w-[19px] items-center justify-center rounded-full border-[1.5px] border-primary bg-background px-1 text-[12px] font-bold text-primary shadow-sm'
                title={`${queueCount} ${queueCount === 1 ? 'group' : 'groups'} ready to build in the CBS queue`}
              >
                {queueCount}
              </span>
            ) : null}
          </div>
        )}
      </PageHeader>

      {isBrokerUser ? (
        <div className='border-b bg-background px-6 py-3'>
          <BrokerScopeControls
            scope={scope}
            onScopeChange={setScope}
            assignedBrokerId={filters.assignedBrokerId}
            onAssignedBrokerChange={(id) => setFilters((f) => ({...f, assignedBrokerId: id}))}
            mineLabel='My accounts'
          />
        </div>
      ) : null}

      {/* Client portals keep the flush filter strip under the header; the
          internal portal renders the filter card below the status board
          (cbs-portal page order). */}
      {!isInternal ? (
        <AccountFilterBar filters={filters} onChange={setFilters} hideBrokerFilter={isBrokerUser} />
      ) : null}

      {/* The internal layout shell caps content at the demo's 1440px centered
          column; client portals keep the original full-width padding. */}
      <div className={isInternal ? 'flex-1 overflow-auto px-6 py-6' : 'flex-1 overflow-auto p-4'}>
        <div className='space-y-4'>
          {/* The status board is an internal-portal surface; client/tenant portals
            keep the main account view (no status board). */}
          {isInternal ? (
            <>
              <AccountsStatusBoard
                stageCounts={counts.data?.stageCounts ?? {}}
                subStageCounts={counts.data?.subStageCounts ?? {}}
                selectedKeys={statusKeys}
                onToggle={toggleStatusKey}
                onClear={() => setStatusKeys([])}
                isLoading={counts.isFetching}
                buildHref={`${routePrefix}/new`}
                queueCount={queueCount}
              />
              <AccountFilterBar
                filters={filters}
                onChange={setFilters}
                clientOptions={clientOptions}
                hideBrokerFilter={isBrokerUser}
                resultCount={{shown: items.length, total: totalCount}}
              />
              {statusChips.length > 0 ? (
                <div className='flex flex-wrap items-center gap-2'>
                  <span className='text-[12px] text-muted-foreground'>Filtered by status</span>
                  {statusChips.map((chip) => (
                    <span
                      key={chip.key}
                      className='inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold'
                      style={{backgroundColor: chip.tone.bg, color: chip.tone.fg}}
                    >
                      {chip.label}
                      <button
                        type='button'
                        onClick={() => toggleStatusKey(chip.key)}
                        className='inline-flex items-center justify-center'
                        aria-label={`Clear ${chip.label} filter`}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              ) : null}
            </>
          ) : null}

          {list.isLoading ? (
            <div className='py-12 text-center text-sm text-muted-foreground'>Loading accounts…</div>
          ) : list.error ? (
            <div className='py-12 text-center text-sm text-red-600'>
              Error loading accounts: {list.error.message}
            </div>
          ) : (
            <>
              {view === 'kanban' ? (
                <AccountKanban
                  accounts={items}
                  stageCounts={counts.data?.stageCounts}
                  routePrefix={routePrefix}
                  onAttemptMove={handleAttemptMove}
                  canReorderWithinPhase={isInternalAdmin}
                  onAttemptSubStageMove={handleAttemptSubStageMove}
                />
              ) : (
                <AccountTable accounts={items} routePrefix={routePrefix} />
              )}

              {isInternal ? (
                /* Demo pagination footer: rows-per-page (25/50/100) + shown/total
                 count. The API is cursor-paginated, so "next page" appends via
                 Load more rather than jumping to a numbered page. */
                <div className='flex items-center justify-between pt-2'>
                  <div className='flex items-center gap-2 text-[12px] text-muted-foreground'>
                    <span>Rows per page</span>
                    <Select value={String(pageSize)} onValueChange={(v) => setPageSize(Number(v))}>
                      <SelectTrigger className='h-8 w-[76px] text-[12px]'>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {INTERNAL_PAGE_SIZES.map((size) => (
                          <SelectItem key={size} value={String(size)}>
                            {size}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <span className='tabular-nums'>
                      Showing {items.length.toLocaleString()} of {totalCount.toLocaleString()}
                    </span>
                  </div>
                  {list.hasNextPage ? (
                    <Button
                      variant='outline'
                      size='sm'
                      onClick={() => list.fetchNextPage()}
                      disabled={list.isFetchingNextPage}
                    >
                      {list.isFetchingNextPage ? 'Loading…' : 'Load more'}
                    </Button>
                  ) : null}
                </div>
              ) : list.hasNextPage ? (
                <div className='flex justify-center pt-2'>
                  <Button
                    variant='outline'
                    size='sm'
                    onClick={() => list.fetchNextPage()}
                    disabled={list.isFetchingNextPage}
                  >
                    {list.isFetchingNextPage ? 'Loading…' : 'Load more'}
                  </Button>
                </div>
              ) : null}
            </>
          )}
        </div>
      </div>

      <NewAccountModal
        open={isNewRoute}
        onClose={() => navigate(routePrefix)}
        routePrefix={routePrefix}
        clientOptions={isInternal ? clientOptions : undefined}
      />

      <SkipConfirmModal
        open={!!pendingMove}
        move={pendingMove}
        onConfirm={handleConfirmMove}
        onCancel={() => setPendingMove(null)}
        confirming={transitionPhase.isPending || setViewSubStage.isPending}
      />
    </div>
  );
}
